import React, { useState, useEffect } from 'react';
import {
    collection, addDoc, getDocs, updateDoc,
    deleteDoc, doc, query, where, getDoc, serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// MUI Components
import {
    Container, Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, InputAdornment, Pagination, CircularProgress, Divider, Stack,
    Grid
} from '@mui/material';
import { 
    Delete as DeleteIcon, 
    Edit as EditIcon, 
    Search as SearchIcon, 
    Add as AddIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';

const ProductCategory = () => {
    const [categories, setCategories] = useState([]);
    const [catName, setCatName] = useState('');
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editId, setEditId] = useState(null);

    // Pagination State
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        // Use an observer to ensure the user is loaded
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const userDoc = await getDoc(doc(db, "users", user.uid));
                    if (userDoc.exists()) {
                        const sId = userDoc.data().schoolId;
                        setSchoolId(sId);
                        fetchCategories(sId);
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const fetchCategories = async (sId) => {
        const q = query(collection(db, "categories"), where("schoolId", "==", sId));
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCategories(data);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!catName.trim()) return;

        try {
            if (editId) {
                await updateDoc(doc(db, "categories", editId), { catName });
                setEditId(null);
            } else {
                await addDoc(collection(db, "categories"), {
                    catName,
                    schoolId,
                    createdAt: serverTimestamp()
                });
            }
            setCatName('');
            fetchCategories(schoolId);
        } catch (error) {
            console.error("Error saving document: ", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this category?")) {
            await deleteDoc(doc(db, "categories", id));
            fetchCategories(schoolId);
        }
    };

    const startEdit = (cat) => {
        setEditId(cat.id);
        setCatName(cat.catName);
    };

    // Filtering & Pagination Logic
    const filteredData = categories.filter(item =>
        item.catName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedItems = filteredData.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Grid sx={{ mt: 1, mb: 2}}>
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                    Product Category Management
                </Typography>

                {/* Input Form */}
                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            fullWidth
                            label="Category Name"
                            variant="outlined"
                            size="small"
                            value={catName}
                            onChange={(e) => setCatName(e.target.value)}
                        />
                        <Button 
                            variant="contained" 
                            type="submit" 
                            startIcon={editId ? <SaveIcon /> : <AddIcon />}
                            sx={{ minWidth: '120px' }}
                        >
                            {editId ? 'Update' : 'Add'}
                        </Button>
                        {editId && (
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={() => { setEditId(null); setCatName(''); }}
                                startIcon={<CancelIcon />}
                            >
                                Cancel
                            </Button>
                        )}
                    </Stack>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Search Header */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <TextField
                        size="small"
                        placeholder="Search categories..."
                        variant="standard"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', sm: '250px' } }}
                    />
                </Box>

                {/* Table */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Sr. No</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Category Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItems.length > 0 ? (
                                paginatedItems.map((cat, index) => (
                                    <TableRow key={cat.id} hover>
                                        <TableCell>{(page - 1) * itemsPerPage + index + 1}</TableCell>
                                        <TableCell>{cat.catName}</TableCell>
                                        <TableCell align="right">
                                            <IconButton color="primary" onClick={() => startEdit(cat)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => handleDelete(cat.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                        No categories found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={(e, value) => setPage(value)} 
                        color="primary" 
                        shape="rounded"
                    />
                </Box>
            </Paper>
        </Grid>
    );
};

export default ProductCategory;